// Small helper for updating document title / meta tags from a page component.
// Tags we create are tagged with data-managed-meta so they can be cleaned up
// when the page unmounts, leaving the index.html defaults intact.

const MANAGED_ATTRIBUTE = 'data-managed-meta';

export const DEFAULT_DOCUMENT_TITLE = 'AtadaBoard';

/** Canonical origin for the marketing site, used to build absolute page URLs. */
export const SITE_ORIGIN = 'https://atadaboard.com';

let snapshot = null;

const findMetaTag = (selectorAttribute, key) =>
  document.head.querySelector(`meta[${selectorAttribute}="${key}"]`);

const findCanonicalTag = () => document.head.querySelector('link[rel="canonical"]');

const takeSnapshot = () => {
  if (snapshot) return;

  const description = findMetaTag('name', 'description');
  const canonical = findCanonicalTag();
  const robots = findMetaTag('name', 'robots');

  snapshot = {
    title: document.title,
    description: description ? description.getAttribute('content') : null,
    canonical: canonical ? canonical.getAttribute('href') : null,
    robots: robots ? robots.getAttribute('content') : null,
  };
};

/**
 * Point the canonical link at this page. index.html ships a canonical for the
 * home page, so every other route must overwrite it or it would tell Google
 * that this page is a duplicate of the landing page.
 */
const setCanonicalUrl = (href) => {
  if (!href) return;

  let tag = findCanonicalTag();

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    tag.setAttribute(MANAGED_ATTRIBUTE, 'true');
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', href);
};

const setMetaTag = (selectorAttribute, key, content) => {
  if (!content) return;

  let tag = findMetaTag(selectorAttribute, key);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(selectorAttribute, key);
    tag.setAttribute(MANAGED_ATTRIBUTE, 'true');
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
};

/**
 * Apply page metadata. Only truthy values are written, so a school without a
 * description or image simply keeps fewer tags rather than empty ones.
 */
export const setDocumentMetadata = ({
  title,
  description,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = 'website',
  canonicalUrl,
  robots,
} = {}) => {
  if (typeof document === 'undefined') return;

  takeSnapshot();

  if (title) document.title = title;

  setMetaTag('name', 'description', description);
  setMetaTag('name', 'robots', robots);
  setCanonicalUrl(canonicalUrl || ogUrl);
  setMetaTag('property', 'og:title', ogTitle || title);
  setMetaTag('property', 'og:description', ogDescription || description);
  setMetaTag('property', 'og:image', ogImage);
  setMetaTag('property', 'og:url', ogUrl);
  setMetaTag('property', 'og:type', ogType);
  setMetaTag('name', 'twitter:card', ogImage ? 'summary_large_image' : 'summary');
};

/** Restore the metadata that was in place before the page mounted. */
export const resetDocumentMetadata = () => {
  if (typeof document === 'undefined' || !snapshot) return;

  document.title = snapshot.title || DEFAULT_DOCUMENT_TITLE;

  const description = findMetaTag('name', 'description');
  if (description) {
    if (snapshot.description === null) {
      description.remove();
    } else {
      description.setAttribute('content', snapshot.description);
    }
  }

  const robots = findMetaTag('name', 'robots');
  if (robots && snapshot.robots !== null) {
    robots.setAttribute('content', snapshot.robots);
  }

  const canonical = findCanonicalTag();
  if (canonical && snapshot.canonical !== null) {
    canonical.setAttribute('href', snapshot.canonical);
  }

  document.head
    .querySelectorAll(`[${MANAGED_ATTRIBUTE}="true"]`)
    .forEach((tag) => tag.remove());

  snapshot = null;
};
